import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, TrendingDown, DollarSign, Target, 
  Shield, Award, AlertTriangle, Users, Zap, ChevronRight
} from 'lucide-react';

// Format currency
const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

// KPI Item Component
const KPIItem = ({ icon: Icon, label, value, subValue, trend, trendLabel, highlight, riskLevel }) => {
  const getTrendColor = () => {
    if (riskLevel === 'high') return 'text-destructive';
    if (riskLevel === 'medium') return 'text-warning';
    if (riskLevel === 'low') return 'text-stable';
    if (trend > 0) return 'text-stable';
    if (trend < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  return (
    <div className={`p-4 rounded-xl ${highlight ? 'bg-primary/5 border-2 border-primary/20' : 'bg-card border border-border/50'}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${highlight ? 'bg-primary/10' : 'bg-muted/50'} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${highlight ? 'text-primary' : 'text-muted-foreground'}`} />
        </div>
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${highlight ? 'text-primary' : 'text-foreground'}`}>{value}</p>
      {subValue && <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>}
      {(trend !== undefined || riskLevel) && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${getTrendColor()}`}>
          {trend !== undefined && (
            <>
              {trend > 0 ? <TrendingUp className="w-3 h-3" /> : trend < 0 ? <TrendingDown className="w-3 h-3" /> : null}
              <span className="font-medium">{trend > 0 ? '+' : ''}{trend}%</span>
              {trendLabel && <span className="text-muted-foreground ml-1">{trendLabel}</span>}
            </>
          )}
          {riskLevel && (
            <Badge className={`text-xs ${
              riskLevel === 'high' ? 'bg-destructive/10 text-destructive border-destructive/30' :
              riskLevel === 'medium' ? 'bg-warning/10 text-warning border-warning/30' :
              'bg-stable/10 text-stable border-stable/30'
            } border`}>
              {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} Risk
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

// Executive KPI Bar Component
export const ExecutiveKPIBar = ({ data, brandMetrics }) => {
  if (!data || !brandMetrics) return null;

  const eco = data.economic_value || {};
  const cycleGrowth = data.historical_cycles?.[data.historical_cycles.length - 1]?.growth_pct || 18.5;
  
  // Calculate risk exposure based on roster concentration
  // The roster data is an object with star_players array, each player has brand_score
  const starPlayers = data.roster_brand_signals?.star_players || [];
  const topPlayerBrandScore = starPlayers[0]?.brand_score || 35;
  // Convert brand_score to a percentage for display (out of 100)
  const topPlayerBrandPct = topPlayerBrandScore;
  const riskLevel = topPlayerBrandPct > 40 ? 'high' : topPlayerBrandPct > 25 ? 'medium' : 'low';

  return (
    <div className="mb-8" data-testid="executive-kpi-bar">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KPIItem
          icon={DollarSign}
          label="Brand Value Range"
          value={`${formatCurrency(eco.range_low)} – ${formatCurrency(eco.range_high)}`}
          trend={eco.year_over_year_change}
          trendLabel="YoY"
          highlight
        />
        <KPIItem
          icon={Target}
          label="Overall Index Score"
          value={brandMetrics.overall_brand_score}
          subValue="/ 100"
        />
        <KPIItem
          icon={TrendingUp}
          label="4-Year Growth"
          value={`+${cycleGrowth}%`}
          subValue="Cycle-over-cycle"
        />
        <KPIItem
          icon={Shield}
          label="Risk Exposure"
          value={`${topPlayerBrandPct}%`}
          subValue="Top player concentration"
          riskLevel={riskLevel}
        />
        <KPIItem
          icon={Award}
          label="Competitive Rank"
          value={`#${data.fifa_ranking || 1}`}
          subValue="CONCACAF Position"
        />
      </div>
    </div>
  );
};

// Section Header Component
export const SectionBlock = ({ icon: Icon, title, description, children, className = '' }) => (
  <section className={`mb-8 ${className}`}>
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
    </div>
    {children}
  </section>
);

// Key Growth Drivers Component
export const KeyGrowthDrivers = ({ data, brandMetrics }) => {
  if (!data) return null;

  // Generate insights based on data
  const insights = [];
  
  // YoY Growth insight
  const yoyChange = data.economic_value?.year_over_year_change || 0;
  if (yoyChange > 15) {
    insights.push({
      type: 'positive',
      text: `Strong year-over-year growth of +${yoyChange}% driven by tournament performance and digital expansion.`
    });
  } else if (yoyChange > 0) {
    insights.push({
      type: 'neutral',
      text: `Modest growth of +${yoyChange}% YoY. Acceleration opportunities in sponsor acquisition and media coverage.`
    });
  }

  // Digital presence insight
  const digitalGrowth = data.digital_presence?.social_growth || 12;
  if (digitalGrowth > 10) {
    insights.push({
      type: 'positive',
      text: `Digital audience growing at ${digitalGrowth}% annually. Social media engagement exceeds confederation average.`
    });
  }

  // Commercial attractiveness
  const commercial = brandMetrics?.commercial_attractiveness || 70;
  if (commercial > 75) {
    insights.push({
      type: 'positive',
      text: `High commercial attractiveness score (${commercial}/100) indicates strong sponsorship market positioning.`
    });
  } else {
    insights.push({
      type: 'neutral',
      text: `Commercial score of ${commercial}/100 suggests opportunity for sponsor portfolio expansion.`
    });
  }

  // World Cup positioning
  insights.push({
    type: 'opportunity',
    text: '2026 World Cup hosting provides structural tailwind for brand value acceleration through 2027.'
  });

  return (
    <SectionBlock
      icon={TrendingUp}
      title="Key Growth Drivers"
      description="Factors contributing to brand value trajectory"
    >
      <Card className="bg-card border-border/50">
        <CardContent className="p-5">
          <ul className="space-y-3">
            {insights.map((insight, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  insight.type === 'positive' ? 'bg-stable/10' :
                  insight.type === 'opportunity' ? 'bg-primary/10' :
                  'bg-warning/10'
                }`}>
                  {insight.type === 'positive' ? (
                    <TrendingUp className="w-3 h-3 text-stable" />
                  ) : insight.type === 'opportunity' ? (
                    <Zap className="w-3 h-3 text-primary" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-warning" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{insight.text}</p>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </SectionBlock>
  );
};

// Risk Concentration Component
export const RiskConcentration = ({ rosterData }) => {
  // Handle case when rosterData is not an array
  if (!rosterData || !Array.isArray(rosterData) || rosterData.length === 0) return null;

  const topPlayers = rosterData.slice(0, 3);
  // Use brand_score from API data (each player has brand_score out of 100)
  const totalConcentration = topPlayers.reduce((sum, p) => sum + (p.brand_score || p.brand_strength_pct || 0), 0);
  const topPlayerPct = topPlayers[0]?.brand_score || topPlayers[0]?.brand_strength_pct || 35;
  const riskLevel = topPlayerPct > 40 ? 'high' : topPlayerPct > 25 ? 'medium' : 'low';

  return (
    <SectionBlock
      icon={AlertTriangle}
      title="Risk Concentration"
      description="Brand value dependency on key roster assets"
    >
      <Card className={`border-2 ${
        riskLevel === 'high' ? 'bg-destructive/5 border-destructive/20' :
        riskLevel === 'medium' ? 'bg-warning/5 border-warning/20' :
        'bg-stable/5 border-stable/20'
      }`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Top 3 Player Concentration</p>
              <p className={`text-2xl font-bold ${
                riskLevel === 'high' ? 'text-destructive' :
                riskLevel === 'medium' ? 'text-warning' :
                'text-stable'
              }`}>{totalConcentration.toFixed(1)}%</p>
            </div>
            <Badge className={`${
              riskLevel === 'high' ? 'bg-destructive/10 text-destructive border-destructive/30' :
              riskLevel === 'medium' ? 'bg-warning/10 text-warning border-warning/30' :
              'bg-stable/10 text-stable border-stable/30'
            } border`}>
              {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} Concentration
            </Badge>
          </div>

          <div className="space-y-3">
            {topPlayers.map((player, i) => {
              const playerBrandPct = player.brand_score || player.brand_strength_pct || 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">{player.name}</span>
                      <span className="text-sm font-bold text-foreground">{playerBrandPct}%</span>
                    </div>
                    <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          i === 0 ? 'bg-primary' : i === 1 ? 'bg-primary/70' : 'bg-primary/40'
                        }`}
                        style={{ width: `${playerBrandPct * 2}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-border/50">
            <p className="text-xs text-muted-foreground">
              {riskLevel === 'high' 
                ? 'High concentration risk. Consider visibility programs for emerging roster talent to diversify brand equity.'
                : riskLevel === 'medium'
                ? 'Moderate concentration. Continue developing secondary brand ambassadors.'
                : 'Healthy distribution across roster. Low single-player dependency risk.'}
            </p>
          </div>
        </CardContent>
      </Card>
    </SectionBlock>
  );
};

// Strategic Outlook Component
export const StrategicOutlook = ({ data, brandMetrics }) => {
  if (!data) return null;

  const currentScore = brandMetrics?.overall_brand_score || 70;
  const yoyGrowth = data.economic_value?.year_over_year_change || 15;
  const projectedScore12M = Math.min(100, Math.round(currentScore * (1 + (yoyGrowth / 200))));
  const projectedScore24M = Math.min(100, Math.round(currentScore * (1 + (yoyGrowth / 100))));
  
  const valueHigh = data.economic_value?.range_high || 340000000;
  const projectedValue12M = Math.round(valueHigh * 1.15);
  const projectedValue24M = Math.round(valueHigh * 1.35);

  return (
    <SectionBlock
      icon={Target}
      title="Strategic Outlook"
      description="12-24 month brand trajectory projection"
    >
      <div className="grid md:grid-cols-2 gap-4">
        {/* 12 Month Projection */}
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-primary/10 text-primary border-primary/30">12 Month</Badge>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Projected Brand Score</p>
                <p className="text-2xl font-bold text-foreground">
                  {currentScore} → <span className="text-stable">{projectedScore12M}</span>
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Projected Value Range</p>
                <p className="text-lg font-bold text-foreground">
                  Up to <span className="text-stable">{formatCurrency(projectedValue12M)}</span>
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Conservative projection assuming steady commercial growth and maintained tournament performance.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 24 Month Projection */}
        <Card className="bg-primary/5 border-primary/20 border-2">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-primary/10 text-primary border-primary/30">24 Month (Post-2026)</Badge>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Projected Brand Score</p>
                <p className="text-2xl font-bold text-foreground">
                  {currentScore} → <span className="text-primary">{projectedScore24M}</span>
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Projected Value Range</p>
                <p className="text-lg font-bold text-foreground">
                  Up to <span className="text-primary">{formatCurrency(projectedValue24M)}</span>
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Includes 2026 World Cup hosting premium. Quarterfinal+ outcome assumed for base case.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </SectionBlock>
  );
};

// Institutional Disclaimer Footer
export const InstitutionalDisclaimer = () => (
  <div className="mt-10 p-6 rounded-xl bg-muted/20 border border-border/50">
    <p className="text-xs text-muted-foreground text-center max-w-4xl mx-auto">
      <strong className="text-foreground">Important:</strong> This analysis is intended for strategic evaluation purposes only and does not constitute a financial valuation. 
      All projections are directional indicators based on publicly available data signals and proprietary analytical models. 
      Actual brand values may vary based on market conditions, tournament outcomes, and other external factors. 
      Figures are intended for internal strategic planning within authorized recipient organizations.
    </p>
  </div>
);

export default {
  ExecutiveKPIBar,
  SectionBlock,
  KeyGrowthDrivers,
  RiskConcentration,
  StrategicOutlook,
  InstitutionalDisclaimer,
};
